import { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
// import { useUser } from '../contexts/userContext';

export const useCandidateData = () => {
  const [candidateData, setCandidateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [interviewPage, setInterviewPage] = useState(1);

  const transformApiData = (apiData, profileData = null) => {
    const transformed = {
      id: apiData.id,
      email: apiData.email,
      first_name: apiData.first_name,
      last_name: apiData.last_name,
      username: apiData.username,
      company: apiData.company || [{ id: 1, name: "Company Name", industry: null }],
      profile: {
        mobile: apiData.profile?.mobile || "Not provided",
        country_code: apiData.profile?.country_code || "+1",
        aadhaar_number: apiData.profile?.aadhaar_number || null,
        aadhaar_card: apiData.profile?.aadhaar_card || null,
        current_photo: apiData.profile?.current_photo || profileData?.profile?.current_photo || profileData?.profile_picture || null,
        is_verified: apiData.profile?.is_verified || false,
        linkedin_url: apiData.profile?.linkedin_url || profileData?.profile?.linkedin_url || profileData?.linkedin_url || "",
        github_url: apiData.profile?.github_url || profileData?.profile?.github_url || profileData?.github_url || "",
      },
      date_joined: apiData.date_joined,
      last_login: apiData.last_login,
      interviews: apiData.interviews.results || [],
      interviewMeta: {
        count: apiData.interviews?.count,
        current_page: apiData.interviews?.current_page,
        num_pages: apiData.interviews?.num_pages,
      },
      statistics: apiData.statistics || {
        total_interviews: 0,
        completed_interviews: 0,
        average_score: 0,
        best_score: 0,
        last_interview_date: null,
      },
    };

    if (profileData) {
      transformed.email = profileData.email || transformed.email;
      transformed.first_name = profileData.firstName || transformed.first_name;
      transformed.last_name = profileData.lastName || transformed.last_name;
    }

    return transformed;
  };

  const fetchCandidateDetails = async (candidateId, page = 1) => {
    try {
      const authToken = localStorage.getItem('token') || localStorage.getItem('adminToken');
      const response = await api.get(`/candidates/${candidateId}/?interview_page=${page}&interview_page_size=10`, {
        headers: {
          'Authorization': `Token ${authToken}`,
        },
      });
      return response.data;
    } catch (err) {
      console.error('Failed to fetch candidate details:', err);
      return null;
    }
  };

  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let cachedUser = null;
      try {
        cachedUser = JSON.parse(localStorage.getItem('user') || localStorage.getItem('adminUser'));
      } catch (e) {
        console.warn('Failed to parse user from local storage');
      }

      const candidateId = cachedUser?.id || cachedUser?.user_id;

      if (candidateId) {
        const authToken = localStorage.getItem('token') || localStorage.getItem('adminToken');
        const [candidateDetails, profileData] = await Promise.all([
          fetchCandidateDetails(candidateId, 1),
          api.get('/profile/', {
            headers: { 'Authorization': `Token ${authToken}` }
          }).then(res => res.data).catch(err => {
            console.error("Failed to fetch profile data:", err);
            return null; // Do not let this call fail the entire process
          })
        ]);

        if (candidateDetails) {
          setCandidateData(transformApiData(candidateDetails, profileData));
        } else {
          setError('Failed to fetch main candidate details.');
        }
      } else {
        setError('Could not determine user ID.');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch initial candidate data');
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMoreInterviews = useCallback(async (page) => {
    if (!candidateData?.id) return;

    try {
      setLoading(true);
      const details = await fetchCandidateDetails(candidateData.id, page);
      if (details) {
        const transformed = transformApiData(details);
        setCandidateData(prevData => {
          const combined = [...(prevData?.interviews || []), ...transformed.interviews];
          const uniqueInterviews = Array.from(new Map(combined.map(item => [item.id, item])).values());
          return {
            ...prevData,
            interviews: uniqueInterviews,
            interviewMeta: transformed.interviewMeta,
          };
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch more interviews');
    } finally {
      setLoading(false);
    }
  }, [candidateData?.id]);


  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    if (interviewPage > 1) {
      fetchMoreInterviews(interviewPage);
    } else if (interviewPage === 1 && candidateData?.interviews?.length > 10) { 
      fetchInitialData();
    }
  }, [interviewPage, fetchMoreInterviews, fetchInitialData, candidateData?.interviews?.length]);

  const updateCandidateData = async (updatedData) => {
    // Implementation omitted for brevity as it remains unchanged
    try {
      setLoading(true);

      const authToken = localStorage.getItem('token') || localStorage.getItem('adminToken');

      // Transform data back to API format for profile update
      const apiPayload = {
        firstName: updatedData.first_name,
        lastName: updatedData.last_name,
      };

      if (updatedData.profile) {
        if (updatedData.profile.mobile !== undefined && updatedData.profile.mobile !== null) {
          apiPayload.mobile = updatedData.profile.mobile;
        }
        if (updatedData.profile.linkedin_url !== undefined && updatedData.profile.linkedin_url !== null) {
          apiPayload.linkedin_url = updatedData.profile.linkedin_url;
        }
        if (updatedData.profile.github_url !== undefined && updatedData.profile.github_url !== null) {
          apiPayload.github_url = updatedData.profile.github_url;
        }
        if (updatedData.profile.country_code) {
          apiPayload.country_code = updatedData.profile.country_code;
        }
      }

      await api.put('/profile/', apiPayload, {
        headers: {
          'Authorization': `Token ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      // Update local state with new data while preserving interviews and other data
      const updatedCandidateData = {
        ...candidateData,
        first_name: updatedData.first_name,
        last_name: updatedData.last_name,
        profile: {
          ...candidateData.profile,
          mobile: updatedData.profile.mobile,
          country_code: updatedData.profile.country_code,
          linkedin_url: updatedData.profile.linkedin_url,
          github_url: updatedData.profile.github_url,
        }
      };

      setCandidateData(updatedCandidateData);
      setError(null);
      return updatedCandidateData;
    } catch (err) {
      setError(err.message || 'Failed to update profile');
      console.error('Failed to update profile:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    candidateData,
    loading,
    error,
    interviewPage,
    setInterviewPage,
    totalPages: candidateData?.interviewMeta?.num_pages || 1,
    updateCandidateData,
    refetchData: fetchInitialData,
  };
};